import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock, ChevronLeft, ChevronRight, Compass, PlayCircle, X, Smartphone, Monitor, Layers,
  UserPlus, LayoutDashboard, BookOpen, Map, Sparkles, Utensils, CloudSun, Users,
  MessageCircle, Trophy, Gift, ShoppingBag, Settings, Gem, LineChart, Sprout,
} from 'lucide-react';

// Homegrown — private, interactive product walkthrough. Passphrase-gated, with a
// guided step-through tour AND a free-explore mode. Screenshots live in /demo-assets.
const DEMO_KEY = 'hg_demo_unlocked';
const PASSPHRASE = 'Lettucehelpyou';
const IMG = (f) => `/demo-assets/${f}`;

const C = {
  forest: '#1A4331', forestDeep: '#0F2D1F', sage: '#E5EBDD', sageDeep: '#D9E1CB',
  cream: '#FAF6EC', creamWarm: '#F4EEDE', lime: '#B4D330', gold: '#C9A85B',
  leaf: '#6B9F4A', leafDeep: '#4E7A33', terracotta: '#C66B4A',
  text: '#2D241E', textMuted: '#5C665C', textSoft: '#7A6B57', white: '#FFFFFF',
  border: 'rgba(26,67,49,0.10)',
};
const OF = { fontFamily: 'Outfit, sans-serif' };
const TAG_TONE = { Both: C.forest, Web: C.gold, Mobile: C.leafDeep };

const CATEGORIES = [
  {
    key: 'accounts', title: 'Accounts & Onboarding', Icon: UserPlus,
    intro: 'A warm, guided start that tailors the whole app to each grower — and a privacy-first stance.',
    features: [
      ['Google sign-in', 'Both', 'Secure one-tap sign-in with a Google account.'],
      ['Email + password login', 'Both', 'For founder, supplier and reviewer accounts.'],
      ['Guided 7-step onboarding', 'Both', 'Location, climate zone, skill level, garden type & size, growing preference, and an AI-coach opt-in.'],
      ['Privacy-first AI opt-out', 'Both', 'Growers can permanently disable every AI feature — and re-enable later via an in-app request.'],
      ['Delete my account', 'Both', 'One-tap, typed-confirmation full data wipe (App Store / Google Play compliant).'],
    ],
  },
  {
    key: 'today', title: 'Today — Daily Command Screen', Icon: LayoutDashboard, hero: 'today.png',
    intro: 'The daily home base — one glance tells a grower exactly what to do today.',
    features: [
      ['Living Sky weather hero', 'Both', 'A mood-driven animated sky (sun, rain, frost, stars) reflecting live conditions.'],
      ['Dispatcher briefing card', 'Both', 'One clear "what to do now" headline with a single-tap action.'],
      ['Garden Intelligence vitals HUD', 'Both', 'Live soil temp, frost watch, UV and next-harvest chips. (Premium)'],
      ["This week's focus", 'Both', 'Tickable priority tasks with progress saved for the week.'],
      ['Growing crops carousel', 'Both', 'Immersive image cards of live crops with stage and days-to-harvest.'],
      ["This week's value", 'Both', 'An estimated dollar value of the produce being grown.'],
      ['What to sow now', 'Both', 'Seasonal sowing suggestions with "window closing" warnings.'],
    ],
  },
  {
    key: 'handbook', title: 'Crop Handbook & Guides', Icon: BookOpen, hero: 'handbook.png',
    gallery: ['handbook.png', 'crop.png'],
    intro: '70+ NZ/AU crops, each a world-class regional guide — the master grower in your pocket.',
    features: [
      ['Master crop library', 'Both', '70+ vegetables and herbs, each with its own photo, difficulty and "sow now" badge.'],
      ['Search & quick filters', 'Both', 'Category, sow-now, beginner-friendly and container-friendly.'],
      ['Rich crop guide', 'Both', 'Climate HUD, sowing-calendar bar chart, vitality HUD and where-to-grow beds.'],
      ['Regional AI growing guide', 'Both', 'Guidance tailored to the exact region and climate zone.'],
      ['Succession strategist', 'Both', 'Staggered planting schedules for a steady supply, not a glut.'],
      ['Companion planting map', 'Both', 'Interactive friends & foes with the strategic "why".'],
      ['Save-for-later shortlist', 'Both', 'Bookmark crops to plan next season.'],
    ],
  },
  {
    key: 'planning', title: 'Garden Planning', Icon: Map,
    intro: 'Everything a grower is growing, planned across the year and tracked to harvest.',
    features: [
      ['My crops / plantings', 'Both', 'Track everything you are growing, organised by growth stage.'],
      ['Multi-area gardens', 'Both', 'Name and manage multiple garden areas (unlimited on Premium).'],
      ['Season planner', 'Both', 'Plan the whole growing year.'],
      ['Task scheduler & reminders', 'Both', 'Time-based to-dos so nothing gets missed.'],
      ['Harvest log & yield tracking', 'Both', 'Record harvests and watch yields build.'],
      ['Data export (CSV)', 'Web', 'Premium export of harvest data.'],
    ],
  },
  {
    key: 'ai', title: 'AI Garden Intelligence', Icon: Sparkles, hero: 'coach.png',
    intro: 'A master grower, agronomist and pest scout — powered by AI, tuned to each garden.',
    features: [
      ['AI Coach chat', 'Both', 'Streaming chat with a master-grower AI. Free = 3/day, members unlimited.'],
      ['Pest & disease scanner', 'Both', 'Snap a photo for an AI diagnosis of plant problems. (Premium)'],
      ['Soil advisor', 'Both', 'AI guidance on soil health and improvement.'],
      ['Harvest photo parser', 'Both', 'Photograph a harvest and have crops auto-logged.'],
    ],
  },
  {
    key: 'pantry', title: 'The Pantry', Icon: Utensils, hero: 'pantry.png',
    intro: 'From garden to plate — zero-waste cooking built from what was actually harvested.',
    features: [
      ['Zero-waste recipes', 'Both', 'AI recipes built from your real harvest. (Premium)'],
      ['Feed my family', 'Both', 'Plan meals straight from the garden. (Premium)'],
      ['Food-security insights', 'Both', 'See how much of your food you are growing. (Premium)'],
    ],
  },
  {
    key: 'weather', title: 'Weather', Icon: CloudSun, hero: 'weather.png',
    intro: 'Hyper-local, global weather with the frost and UV alerts that matter to a garden.',
    features: [
      ['Live weather & forecast', 'Both', 'Global local weather via Open-Meteo, with frost and UV alerts.'],
    ],
  },
  {
    key: 'community', title: 'Community', Icon: Users, hero: 'community.png',
    intro: 'A living regional feed — the social heart that keeps growers coming back daily.',
    features: [
      ['Regional feed & pulse', 'Both', 'Posts from growers in your region; double-tap to like.'],
      ['Harvest photo & video posts', 'Both', 'Share wins with photos and video.'],
      ['Grower profiles & gallery', 'Both', 'A season / plant-family grouped photo & video gallery.'],
      ['Leaderboards + "Your Rank"', 'Both', 'XP and harvest boards with your rank pinned.'],
      ['Challenges', 'Both', 'Opt-in community challenges that earn XP.'],
      ['Peer Swap Market', 'Both', 'List and request seed/produce swaps.'],
      ['Report-a-post moderation', 'Both', 'Auto-hides a post after three reports.'],
    ],
  },
  {
    key: 'dms', title: 'Direct Messages', Icon: MessageCircle,
    intro: 'Private grower-to-grower chat — rich media, receipts, and share-a-harvest.',
    features: [
      ['Text, photo & video DMs', 'Both', 'Message growers you mutually follow; multi-photo galleries on web.'],
      ['Voice notes (up to 60s)', 'Mobile', 'Record and play voice notes inline (device only).'],
      ['Read receipts', 'Mobile', 'Sent → seen double-tick.'],
      ['Tap-to-zoom media', 'Mobile', 'Pinch, pan and double-tap to zoom photos.'],
      ['Share a harvest to a grower', 'Both', 'Forward a community post into a DM to start a swap.'],
    ],
  },
  {
    key: 'rewards', title: 'Rewards & Progress', Icon: Trophy, hero: 'rewards.png',
    intro: 'Playful progression that turns everyday gardening into levels, skills and badges.',
    features: [
      ['XP & levels', 'Both', 'Earn experience and level up as you grow.'],
      ['Skill tree', 'Both', 'Unlock grower skills over time.'],
      ['Achievements & badges', 'Both', 'Collect badges with a full-screen celebration and share.'],
      ['Quests', 'Both', 'Guided goals that reward progress.'],
    ],
  },
  {
    key: 'collective', title: 'Homegrown Collective', Icon: Gift, hero: 'collective.png',
    intro: 'Our loyalty engine — one points wallet, four tiers, real rewards and a monetisable partner network.',
    features: [
      ['Points wallet & 4 tiers', 'Both', 'Sprout → Grower → Cultivator → Homesteader; earn on spend.'],
      ['Growing-plant growth meter', 'Both', 'An animated plant that grows toward the next tier.'],
      ['Rewards store', 'Both', 'Redeem points for real rewards.'],
      ['Partner offers', 'Both', 'Member-only partner codes and discounts.'],
      ['Exclusive events + RSVP', 'Both', 'Tier-gated events.'],
      ['Refer a friend', 'Both', 'Double-sided referral bonuses.'],
      ['Digital membership card', 'Both', 'Scannable QR + barcode, brightness-boost scan mode on mobile.'],
      ['Birthday reward', 'Both', 'A personalised note and bonus points from the founders.'],
    ],
  },
  {
    key: 'shop', title: 'Shop & Commerce', Icon: ShoppingBag, hero: 'shop.png',
    intro: 'A boutique storefront and full checkout — the app monetises beyond subscriptions.',
    features: [
      ['Boutique storefront', 'Both', 'A curated seed & product store with an image-forward grid.'],
      ['Product detail + formats', 'Both', 'Variant / format selector with live pricing.'],
      ['Wishlist & recommendations', 'Both', 'Save products; a personalised "for you" rail.'],
      ['Cart + member 20% discount', 'Both', 'Members automatically save 20%.'],
      ['Stripe checkout + Express Pay', 'Both', 'Secure hosted checkout with Apple/Express Pay.'],
      ['My Orders + delivery timeline', 'Both', 'Uber-style tracking, reorder, and a digital library.'],
      ['Order tracking & shipping alerts', 'Both', 'Carrier detection, auto status and in-app notifications.'],
    ],
  },
  {
    key: 'settings', title: 'Settings & Privacy', Icon: Settings,
    intro: 'Deep control — including a calm-first "Zen Mode" that sets Homegrown apart.',
    features: [
      ['Zen Mode', 'Mobile', 'Hide the AI Coach, Community and Levels/XP for a calm app.'],
      ['Zen Schedule', 'Mobile', 'Auto-switch Zen Mode on during a set time window on chosen days.'],
      ['Feature toggles', 'Both', 'Turn AI Coach, Intelligence, Skill Tree and Challenges on/off.'],
      ['Customisable quick actions', 'Web', 'Unlimited, reorderable quick-action tiles.'],
      ['Legal & support', 'Both', 'Terms, Privacy and contact support.'],
    ],
  },
  {
    key: 'premium', title: 'Premium & Subscriptions', Icon: Gem,
    intro: 'A clean two-tier model with elegant upgrade moments throughout the app.',
    features: [
      ['Free / Premium tiers', 'Both', 'A 7-day free trial; Premium unlocks the full garden — multi-area gardens and data export included.'],
      ['Premium access matrix', 'Both', 'Elegant frosted upgrade gates on premium modules.'],
    ],
  },
  {
    key: 'founder', title: "Founder's Executive Suite", Icon: LineChart, hero: 'founder-financials.png',
    gallery: ['founder-financials.png', 'founder-customers.png', 'founder-inventory.png', 'command.png'],
    locked: true,
    intro: 'Private, vault-gated. The business cockpit — money, customers, stock and B2B logistics in one place.',
    features: [
      ['Founder Vault', 'Both', 'A secondary-password + 2FA gate protecting all founder tools.'],
      ['Executive Suite tabs', 'Both', 'Home, Financials, Customers and Inventory in one command centre.'],
      ['Financials engine', 'Both', 'P&L waterfall, MRR chart, SaaS-multiple valuation and an investment modeler.'],
      ['Investor One-Pager PDF', 'Web', 'Export a branded 1-page investor/bank snapshot on demand.'],
      ['Customers analytics', 'Both', 'Demographics, an at-risk churn pulse and an XP leaderboard.'],
      ['Inventory analytics', 'Both', 'Stock levels, days-of-cover, margins and restock alerts.'],
      ['Founder Intelligence terminal', 'Mobile', 'Retention, revenue, usage, acquisition and weekly priorities.'],
      ['Supplier Command Center (B2B)', 'Both', 'Fulfilment HUD, a regional demand heatmap and partner ROI.'],
    ],
  },
];

const TOTAL_FEATURES = CATEGORIES.reduce((n, c) => n + c.features.length, 0);

/* ---------------- badges & bits ---------------- */
const PlatformBadge = ({ p }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#fff', background: TAG_TONE[p], borderRadius: 999, padding: '3px 9px' }}>
    {p === 'Web' ? <Monitor size={11} /> : p === 'Mobile' ? <Smartphone size={11} /> : <Layers size={11} />}{p}
  </span>
);

const HeroImage = ({ file, Icon, alt }) => (
  file ? (
    <img src={IMG(file)} alt={alt} data-testid="demo-hero-img"
      style={{ width: '100%', borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: '0 16px 40px rgba(15,45,31,0.18)', display: 'block' }} />
  ) : (
    <div style={{ width: '100%', aspectRatio: '16/9', borderRadius: 16, background: `linear-gradient(135deg, ${C.forest}, ${C.forestDeep})`, display: 'grid', placeItems: 'center', boxShadow: '0 16px 40px rgba(15,45,31,0.18)' }}>
      <Icon size={64} color={C.lime} strokeWidth={1.4} />
    </div>
  )
);

/* ---------------- Feature list ---------------- */
const FeatureList = ({ features, dark }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    {features.map(([name, p, desc]) => (
      <div key={name} data-testid="demo-feature" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ marginTop: 6, width: 7, height: 7, borderRadius: 999, background: C.lime, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ ...OF, fontSize: 15, fontWeight: 600, color: dark ? C.cream : C.text }}>{name}</span>
            <PlatformBadge p={p} />
          </div>
          <p style={{ fontSize: 13.5, lineHeight: 1.5, color: dark ? 'rgba(250,246,236,0.72)' : C.textMuted, margin: '2px 0 0' }}>{desc}</p>
        </div>
      </div>
    ))}
  </div>
);

/* ==================================================================== */
export default function Demo() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(DEMO_KEY) === '1');
  if (!unlocked) return <Gate onUnlock={() => { sessionStorage.setItem(DEMO_KEY, '1'); setUnlocked(true); }} />;
  return <Walkthrough />;
}

/* ---------------- Passphrase gate ---------------- */
function Gate({ onUnlock }) {
  const [word, setWord] = useState('');
  const [err, setErr] = useState(false);
  const submit = () => {
    if (word.trim() === PASSPHRASE) onUnlock();
    else setErr(true);
  };
  return (
    <div style={{ minHeight: '100vh', background: C.forestDeep, display: 'grid', placeItems: 'center', padding: 24 }} data-testid="demo-gate">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        style={{ width: '100%', maxWidth: 400, background: C.cream, borderRadius: 24, padding: 36, boxShadow: '0 24px 70px rgba(0,0,0,0.35)', textAlign: 'center' }}>
        <div style={{ width: 60, height: 60, borderRadius: 18, background: C.forest, display: 'grid', placeItems: 'center', margin: '0 auto 18px' }}>
          <Sprout size={28} color={C.lime} />
        </div>
        <h1 style={{ ...OF, fontSize: 25, fontWeight: 700, color: C.forest, margin: 0 }}>Homegrown</h1>
        <p style={{ ...OF, fontSize: 14, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.gold, margin: '4px 0 0' }}>Product Walkthrough</p>
        <p style={{ fontSize: 14, color: C.textMuted, margin: '14px 0 22px', lineHeight: 1.5 }}>A private preview for the founding team. Enter the passphrase to continue.</p>
        <div style={{ position: 'relative' }}>
          <Lock size={16} style={{ position: 'absolute', left: 14, top: 15, color: C.textMuted }} />
          <input value={word} onChange={(e) => { setWord(e.target.value); setErr(false); }} type="password" placeholder="Passphrase"
            data-testid="demo-passphrase" onKeyDown={(e) => e.key === 'Enter' && submit()}
            style={{ width: '100%', padding: '13px 16px 13px 40px', borderRadius: 12, border: `1px solid ${err ? C.terracotta : 'rgba(26,67,49,0.15)'}`, background: '#fff', outline: 'none', fontSize: 15, boxSizing: 'border-box' }} />
        </div>
        {err && <p style={{ fontSize: 13, color: C.terracotta, margin: '10px 0 0' }} data-testid="demo-gate-error">That is not the passphrase — try again.</p>}
        <button onClick={submit} data-testid="demo-unlock" style={{ width: '100%', marginTop: 18, background: C.forest, color: '#fff', fontWeight: 600, fontSize: 15, padding: '13px 0', borderRadius: 999, border: 'none', cursor: 'pointer' }}>
          Enter walkthrough
        </button>
      </motion.div>
    </div>
  );
}

/* ---------------- Main walkthrough (explore + tour) ---------------- */
function Walkthrough() {
  const [mode, setMode] = useState('explore'); // 'explore' | 'tour'
  const [active, setActive] = useState('accounts');
  const [tourStep, setTourStep] = useState(0);

  const startTour = () => { setTourStep(0); setMode('tour'); };

  return (
    <div style={{ minHeight: '100vh', background: C.sage }} data-testid="demo-walkthrough">
      {/* top bar */}
      <div style={{ background: C.forestDeep, color: C.cream, padding: '16px 24px', position: 'sticky', top: 0, zIndex: 20 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: C.forest, display: 'grid', placeItems: 'center' }}><Sprout size={20} color={C.lime} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ ...OF, fontSize: 18, fontWeight: 700, margin: 0, lineHeight: 1.1 }}>Homegrown</p>
            <p style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.lime, margin: 0 }}>Product Walkthrough</p>
          </div>
          <div style={{ display: 'flex', gap: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 999, padding: 4 }}>
            <ModeBtn active={mode === 'explore'} onClick={() => setMode('explore')} Icon={Compass} label="Explore" testId="demo-mode-explore" />
            <ModeBtn active={mode === 'tour'} onClick={startTour} Icon={PlayCircle} label="Guided tour" testId="demo-mode-tour" />
          </div>
        </div>
      </div>

      {mode === 'explore'
        ? <Explore active={active} setActive={setActive} onStartTour={startTour} />
        : <Tour step={tourStep} setStep={setTourStep} onExit={() => setMode('explore')} />}
    </div>
  );
}

const ModeBtn = ({ active, onClick, Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId}
    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13.5, fontWeight: 600, padding: '8px 14px', borderRadius: 999, border: 'none', cursor: 'pointer', background: active ? C.lime : 'transparent', color: active ? C.forestDeep : C.cream }}>
    <Icon size={16} /> {label}
  </button>
);

/* ---------------- Explore mode ---------------- */
function Explore({ active, setActive, onStartTour }) {
  const cat = CATEGORIES.find((c) => c.key === active) || CATEGORIES[0];
  const [lightbox, setLightbox] = useState(null);
  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24, display: 'grid', gridTemplateColumns: '260px 1fr', gap: 24, alignItems: 'start' }}>
      {/* category rail */}
      <div style={{ position: 'sticky', top: 88, background: C.cream, borderRadius: 18, border: `1px solid ${C.border}`, padding: 10 }} data-testid="demo-category-rail">
        <p style={{ ...OF, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textSoft, margin: '8px 10px 6px' }}>{CATEGORIES.length} feature areas</p>
        {CATEGORIES.map((c) => {
          const on = c.key === active;
          return (
            <button key={c.key} onClick={() => setActive(c.key)} data-testid={`demo-cat-${c.key}`}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', borderRadius: 12, border: 'none', cursor: 'pointer', textAlign: 'left', marginBottom: 2, background: on ? C.forest : 'transparent', transition: 'background 0.15s' }}>
              <c.Icon size={17} color={on ? C.lime : C.forest} />
              <span style={{ fontSize: 13.5, fontWeight: on ? 600 : 500, color: on ? C.cream : C.text, flex: 1 }}>{c.title}</span>
              {c.locked && <Lock size={13} color={on ? C.gold : C.textMuted} />}
            </button>
          );
        })}
        <button onClick={onStartTour} data-testid="demo-explore-starttour"
          style={{ width: '100%', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 0', borderRadius: 12, border: `1px dashed ${C.gold}`, background: 'transparent', color: C.forest, fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>
          <PlayCircle size={16} /> Play guided tour
        </button>
      </div>

      {/* detail panel */}
      <AnimatePresence mode="wait">
        <motion.div key={cat.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
          data-testid="demo-detail">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: C.forest, display: 'grid', placeItems: 'center' }}><cat.Icon size={22} color={C.lime} /></div>
            <div>
              <h2 style={{ ...OF, fontSize: 26, fontWeight: 700, color: C.forest, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                {cat.title}{cat.locked && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: C.gold, background: 'rgba(201,168,91,0.15)', borderRadius: 999, padding: '3px 9px' }}><Lock size={11} /> PRIVATE</span>}
              </h2>
            </div>
          </div>
          <p style={{ fontSize: 15, color: C.textMuted, lineHeight: 1.55, margin: '0 0 18px', maxWidth: 680 }}>{cat.intro}</p>

          {/* gallery or hero */}
          {cat.gallery ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
              {cat.gallery.map((f) => (
                <img key={f} src={IMG(f)} alt={cat.title} onClick={() => setLightbox(f)} data-testid="demo-gallery-img"
                  style={{ width: '100%', borderRadius: 14, border: `1px solid ${C.border}`, cursor: 'zoom-in', boxShadow: '0 10px 26px rgba(15,45,31,0.14)' }} />
              ))}
            </div>
          ) : (
            <div onClick={() => cat.hero && setLightbox(cat.hero)} style={{ marginBottom: 20, cursor: cat.hero ? 'zoom-in' : 'default' }}>
              <HeroImage file={cat.hero} Icon={cat.Icon} alt={cat.title} />
            </div>
          )}

          <div style={{ background: C.cream, borderRadius: 18, border: `1px solid ${C.border}`, padding: 22 }}>
            <FeatureList features={cat.features} />
          </div>
        </motion.div>
      </AnimatePresence>

      {lightbox && (
        <div onClick={() => setLightbox(null)} data-testid="demo-lightbox"
          style={{ position: 'fixed', inset: 0, background: 'rgba(9,26,18,0.88)', display: 'grid', placeItems: 'center', zIndex: 50, padding: 32, cursor: 'zoom-out' }}>
          <img src={IMG(lightbox)} alt="" style={{ maxWidth: '92%', maxHeight: '90%', borderRadius: 12, boxShadow: '0 30px 80px rgba(0,0,0,0.5)' }} />
          <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 999, width: 42, height: 42, display: 'grid', placeItems: 'center', cursor: 'pointer' }}><X size={22} color="#fff" /></button>
        </div>
      )}
    </div>
  );
}

/* ---------------- Guided tour mode ---------------- */
function Tour({ step, setStep, onExit }) {
  // slides: intro + one per category + outro
  const slides = ['intro', ...CATEGORIES.map((c) => c.key), 'outro'];
  const total = slides.length;
  const clamped = Math.max(0, Math.min(step, total - 1));
  const next = useCallback(() => setStep((s) => Math.min(s + 1, total - 1)), [setStep, total]);
  const prev = useCallback(() => setStep((s) => Math.max(s - 1, 0)), [setStep]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'ArrowRight') next(); if (e.key === 'ArrowLeft') prev(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  const slide = slides[clamped];
  const cat = CATEGORIES.find((c) => c.key === slide);

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: 24 }} data-testid="demo-tour">
      {/* progress */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 999, background: C.sageDeep, overflow: 'hidden' }}>
          <motion.div animate={{ width: `${((clamped + 1) / total) * 100}%` }} style={{ height: 6, background: C.lime, borderRadius: 999 }} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.textMuted }} data-testid="demo-tour-progress">{clamped + 1} / {total}</span>
        <button onClick={onExit} data-testid="demo-tour-exit" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: C.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}><X size={14} /> Exit</button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={slide} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.28 }}>
          {slide === 'intro' && (
            <div style={{ background: `linear-gradient(135deg, ${C.forest}, ${C.forestDeep})`, borderRadius: 24, padding: '64px 40px', textAlign: 'center', color: C.cream, minHeight: 420, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ width: 66, height: 66, borderRadius: 20, background: 'rgba(180,211,48,0.18)', display: 'grid', placeItems: 'center', margin: '0 auto 20px' }}><Sprout size={32} color={C.lime} /></div>
              <h1 style={{ ...OF, fontSize: 40, fontWeight: 700, margin: 0 }}>Welcome to Homegrown</h1>
              <p style={{ fontSize: 17, color: 'rgba(250,246,236,0.8)', maxWidth: 620, margin: '16px auto 0', lineHeight: 1.6 }}>
                A guided tour of everything we have built — a premium AI vegetable-growing platform across web and mobile.
                {' '}Use <b>Next</b> (or the → arrow key) to move through {CATEGORIES.length} feature areas and {TOTAL_FEATURES}+ features.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 26, flexWrap: 'wrap' }}>
                <PlatformBadge p="Both" /><PlatformBadge p="Web" /><PlatformBadge p="Mobile" />
              </div>
            </div>
          )}

          {cat && (
            <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 28, alignItems: 'center', minHeight: 420 }} className="demo-tour-grid">
              <div onClick={() => {}} >
                {cat.gallery ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
                    {cat.gallery.slice(0, 4).map((f) => (
                      <img key={f} src={IMG(f)} alt={cat.title} style={{ width: '100%', borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: '0 12px 30px rgba(15,45,31,0.16)' }} />
                    ))}
                  </div>
                ) : <HeroImage file={cat.hero} Icon={cat.Icon} alt={cat.title} />}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 11, background: C.forest, display: 'grid', placeItems: 'center' }}><cat.Icon size={20} color={C.lime} /></div>
                  {cat.locked && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: C.gold, background: 'rgba(201,168,91,0.15)', borderRadius: 999, padding: '3px 9px' }}><Lock size={11} /> PRIVATE</span>}
                </div>
                <h2 style={{ ...OF, fontSize: 27, fontWeight: 700, color: C.forest, margin: '0 0 6px' }}>{cat.title}</h2>
                <p style={{ fontSize: 14.5, color: C.textMuted, lineHeight: 1.55, margin: '0 0 18px' }}>{cat.intro}</p>
                <div style={{ maxHeight: 300, overflowY: 'auto', paddingRight: 6 }}><FeatureList features={cat.features} /></div>
              </div>
            </div>
          )}

          {slide === 'outro' && (
            <div style={{ background: C.cream, borderRadius: 24, padding: '64px 40px', textAlign: 'center', minHeight: 420, display: 'flex', flexDirection: 'column', justifyContent: 'center', border: `1px solid ${C.border}` }}>
              <div style={{ width: 66, height: 66, borderRadius: 20, background: C.forest, display: 'grid', placeItems: 'center', margin: '0 auto 20px' }}><Trophy size={30} color={C.lime} /></div>
              <h1 style={{ ...OF, fontSize: 34, fontWeight: 700, color: C.forest, margin: 0 }}>That is the full picture</h1>
              <p style={{ fontSize: 16, color: C.textMuted, maxWidth: 600, margin: '14px auto 0', lineHeight: 1.6 }}>
                {CATEGORIES.length} feature areas, {TOTAL_FEATURES}+ features, one shared backend across web and mobile.
                Jump back into <b>Explore</b> to dig into any area.
              </p>
              <button onClick={onExit} data-testid="demo-outro-explore" style={{ margin: '26px auto 0', display: 'inline-flex', alignItems: 'center', gap: 8, background: C.forest, color: '#fff', fontWeight: 600, fontSize: 15, padding: '13px 28px', borderRadius: 999, border: 'none', cursor: 'pointer' }}>
                <Compass size={17} /> Explore features
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 22 }}>
        <button onClick={prev} disabled={clamped === 0} data-testid="demo-tour-prev"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14.5, fontWeight: 600, padding: '11px 20px', borderRadius: 999, border: `1px solid ${C.border}`, background: C.cream, color: C.forest, cursor: clamped === 0 ? 'default' : 'pointer', opacity: clamped === 0 ? 0.4 : 1 }}>
          <ChevronLeft size={18} /> Back
        </button>
        <div style={{ display: 'flex', gap: 5 }}>
          {slides.map((s, i) => (
            <span key={s} onClick={() => setStep(i)} style={{ width: i === clamped ? 22 : 8, height: 8, borderRadius: 999, background: i === clamped ? C.forest : C.sageDeep, cursor: 'pointer', transition: 'width 0.2s' }} />
          ))}
        </div>
        <button onClick={next} disabled={clamped === total - 1} data-testid="demo-tour-next"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14.5, fontWeight: 600, padding: '11px 22px', borderRadius: 999, border: 'none', background: C.forest, color: '#fff', cursor: clamped === total - 1 ? 'default' : 'pointer', opacity: clamped === total - 1 ? 0.4 : 1 }}>
          Next <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}
